import React, { useState, useEffect } from 'react';
import {
  Container, Paper, Typography, Box, Grid, TextField,
  Button, MenuItem, Select, FormControl, InputLabel,
  Alert, CircularProgress, Chip, Divider, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, Tabs, Tab,
  Card, CardContent, Accordion, AccordionSummary, AccordionDetails,
  List, ListItem, ListItemIcon, ListItemText, Avatar
} from '@mui/material';
import {
  ArrowBack, Send, ConfirmationNumber, CheckCircle,
  Schedule, HourglassEmpty, ExpandMore,
  SmartToy, Flag, ViewKanban, BarChart, Group, ImportExport,
  Timeline, ChatBubble, Assignment, AutoAwesome, Bolt,
  NewReleases, QuestionAnswer, PlayCircle, FiberNew
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ticketService } from '../services/api';

// ─── Constantes ──────────────────────────────────────────────────────────────

const PRIORITY_COLORS = { urgent: 'error', high: 'warning', medium: 'info', low: 'success' };
const STATUS_ICONS = {
  new: <HourglassEmpty fontSize="small" />,
  open: <Schedule fontSize="small" />,
  pending: <Schedule fontSize="small" />,
  closed: <CheckCircle fontSize="small" />
};
const STATUS_COLORS = { new: 'error', open: 'warning', pending: 'info', closed: 'success' };

const EMPTY_FORM = { subject: '', message: '', priority: 'medium', department: 'general' };

// ─── Composants ───────────────────────────────────────────────────────────────

function StatCard({ label, value, color }) {
  return (
    <Card variant="outlined">
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="h5" fontWeight="bold" color={color + '.main'}>{value}</Typography>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
      </CardContent>
    </Card>
  );
}

// ─── Page principale ──────────────────────────────────────────────────────────

export default function HelpDeskPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [tab, setTab] = useState(0);
  const [form, setForm] = useState({ ...EMPTY_FORM, ...(location.state?.prefill || {}) });
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ type: '', text: '' });
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [viewTicket, setViewTicket] = useState(null);
  const [faqCategory, setFaqCategory] = useState(0);

  // ── Labels traduits ──
  const PRIORITY_LABELS = {
    urgent: t('helpdesk.priorityUrgent'),
    high: t('helpdesk.priorityHigh'),
    medium: t('helpdesk.priorityMedium'),
    low: t('helpdesk.priorityLow')
  };
  const STATUS_LABELS = {
    new: t('helpdesk.statusNew'),
    open: t('helpdesk.statusOpen'),
    pending: t('helpdesk.statusPending'),
    closed: t('helpdesk.statusClosed')
  };
  const DEPT_LABELS = {
    technical: t('helpdesk.deptTechnical'),
    billing: t('helpdesk.deptBilling'),
    sales: t('helpdesk.deptSales'),
    general: t('helpdesk.deptGeneral')
  };

  // ── Nouveautés (traduites) ──
  const NOUVEAUTES = [
    {
      icon: <SmartToy />,
      color: '#6366f1',
      title: t('helpdesk.n1Title'),
      badge: t('helpdesk.badgeNew'),
      description: t('helpdesk.n1Desc'),
      exemples: [
        t('helpdesk.n1Ex1'), t('helpdesk.n1Ex2'), t('helpdesk.n1Ex3'),
        t('helpdesk.n1Ex4'), t('helpdesk.n1Ex5'),
      ],
    },
    {
      icon: <Flag />,
      color: '#9c27b0',
      title: t('helpdesk.n2Title'),
      badge: t('helpdesk.badgeNew'),
      description: t('helpdesk.n2Desc'),
      exemples: [t('helpdesk.n2Ex1'), t('helpdesk.n2Ex2'), t('helpdesk.n2Ex3')],
    },
    {
      icon: <ImportExport />,
      color: '#2196f3',
      title: t('helpdesk.n3Title'),
      badge: t('helpdesk.badgeImproved'),
      description: t('helpdesk.n3Desc'),
      exemples: [
        t('helpdesk.n3Ex1'), t('helpdesk.n3Ex2'), t('helpdesk.n3Ex3'), t('helpdesk.n3Ex4'),
      ],
    },
    {
      icon: <ViewKanban />,
      color: '#4caf50',
      title: t('helpdesk.n4Title'),
      badge: t('helpdesk.badgeImproved'),
      description: t('helpdesk.n4Desc'),
      exemples: [t('helpdesk.n4Ex1'), t('helpdesk.n4Ex2'), t('helpdesk.n4Ex3')],
    },
    {
      icon: <ChatBubble />,
      color: '#ff9800',
      title: t('helpdesk.n5Title'),
      badge: t('helpdesk.badgeImproved'),
      description: t('helpdesk.n5Desc'),
      exemples: [t('helpdesk.n5Ex1'), t('helpdesk.n5Ex2'), t('helpdesk.n5Ex3')],
    },
    {
      icon: <Group />,
      color: '#00bcd4',
      title: t('helpdesk.n6Title'),
      badge: t('helpdesk.badgeAvailable'),
      description: t('helpdesk.n6Desc'),
      exemples: [t('helpdesk.n6Ex1'), t('helpdesk.n6Ex2'), t('helpdesk.n6Ex3')],
    },
  ];

  // ── FAQ (traduite) ──
  const FAQ_CATEGORIES = [
    {
      label: t('helpdesk.faqCatProjects'),
      icon: <Assignment />,
      color: '#6366f1',
      items: [
        { q: t('helpdesk.faqQ1_1'), a: t('helpdesk.faqA1_1') },
        { q: t('helpdesk.faqQ1_2'), a: t('helpdesk.faqA1_2') },
        { q: t('helpdesk.faqQ1_3'), a: t('helpdesk.faqA1_3') },
        { q: t('helpdesk.faqQ1_4'), a: t('helpdesk.faqA1_4') },
      ]
    },
    {
      label: t('helpdesk.faqCatTasks'),
      icon: <Assignment sx={{ fontSize: 20 }} />,
      color: '#2196f3',
      items: [
        { q: t('helpdesk.faqQ2_1'), a: t('helpdesk.faqA2_1') },
        { q: t('helpdesk.faqQ2_2'), a: t('helpdesk.faqA2_2') },
        { q: t('helpdesk.faqQ2_3'), a: t('helpdesk.faqA2_3') },
        { q: t('helpdesk.faqQ2_4'), a: t('helpdesk.faqA2_4') },
      ]
    },
    {
      label: t('helpdesk.faqCatViews'),
      icon: <BarChart />,
      color: '#4caf50',
      items: [
        { q: t('helpdesk.faqQ3_1'), a: t('helpdesk.faqA3_1') },
        { q: t('helpdesk.faqQ3_2'), a: t('helpdesk.faqA3_2') },
        { q: t('helpdesk.faqQ3_3'), a: t('helpdesk.faqA3_3') },
        { q: t('helpdesk.faqQ3_4'), a: t('helpdesk.faqA3_4') },
      ]
    },
    {
      label: t('helpdesk.faqCatAI'),
      icon: <SmartToy />,
      color: '#a855f7',
      items: [
        { q: t('helpdesk.faqQ4_1'), a: t('helpdesk.faqA4_1') },
        { q: t('helpdesk.faqQ4_2'), a: t('helpdesk.faqA4_2') },
        { q: t('helpdesk.faqQ4_3'), a: t('helpdesk.faqA4_3') },
        { q: t('helpdesk.faqQ4_4'), a: t('helpdesk.faqA4_4') },
      ]
    },
    {
      label: t('helpdesk.faqCatImport'),
      icon: <ImportExport />,
      color: '#ff9800',
      items: [
        { q: t('helpdesk.faqQ5_1'), a: t('helpdesk.faqA5_1') },
        { q: t('helpdesk.faqQ5_2'), a: t('helpdesk.faqA5_2') },
        { q: t('helpdesk.faqQ5_3'), a: t('helpdesk.faqA5_3') },
        { q: t('helpdesk.faqQ5_4'), a: t('helpdesk.faqA5_4') },
      ]
    },
    {
      label: t('helpdesk.faqCatAccount'),
      icon: <Bolt />,
      color: '#f44336',
      items: [
        { q: t('helpdesk.faqQ6_1'), a: t('helpdesk.faqA6_1') },
        { q: t('helpdesk.faqQ6_2'), a: t('helpdesk.faqA6_2') },
        { q: t('helpdesk.faqQ6_3'), a: t('helpdesk.faqA6_3') },
        { q: t('helpdesk.faqQ6_4'), a: t('helpdesk.faqA6_4') },
      ]
    },
  ];

  const showAlert = (type, text) => {
    setAlert({ type, text });
    setTimeout(() => setAlert({ type: '', text: '' }), 6000);
  };

  const loadTickets = () => {
    setTicketsLoading(true);
    ticketService.getMyTickets()
      .then(r => setTickets(r.data.tickets || []))
      .catch(() => {})
      .finally(() => setTicketsLoading(false));
  };

  useEffect(() => { loadTickets(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.message.trim()) {
      return showAlert('error', t('helpdesk.formValidation'));
    }
    setLoading(true);
    try {
      const res = await ticketService.submit(form);
      showAlert('success', t('helpdesk.ticketSuccess', { id: res.data.ticketId }));
      setForm(EMPTY_FORM);
      loadTickets();
      setTimeout(() => setTab(1), 1800);
    } catch (err) {
      showAlert('error', err.response?.data?.error || t('helpdesk.submitError'));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d) => new Date(d).toLocaleDateString(undefined, {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });

  const stats = {
    total: tickets.length,
    open: tickets.filter(t => ['new', 'open'].includes(t.status)).length,
    pending: tickets.filter(t => t.status === 'pending').length,
    closed: tickets.filter(t => t.status === 'closed').length,
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 6 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/dashboard')} sx={{ mb: 2 }}>
        {t('common.dashboard')}
      </Button>

      {/* ── Hero ── */}
      <Paper elevation={0} sx={{
        p: { xs: 3, md: 4 }, mb: 3, borderRadius: 3,
        background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
        color: 'white', position: 'relative', overflow: 'hidden'
      }}>
        <Box sx={{ position: 'absolute', right: -20, top: -20, opacity: 0.08, fontSize: 200, lineHeight: 1 }}>
          🎯
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
          <ConfirmationNumber sx={{ fontSize: 36 }} />
          <Box>
            <Typography variant="h4" fontWeight="bold">{t('helpdesk.heroTitle')}</Typography>
            <Typography variant="body2" sx={{ opacity: 0.85 }}>
              {t('helpdesk.heroSubtitle')}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
          {[
            { icon: '🤖', label: t('helpdesk.chip1') },
            { icon: '🏁', label: t('helpdesk.chip2') },
            { icon: '📦', label: t('helpdesk.chip3') },
            { icon: '🔄', label: t('helpdesk.chip4') },
            { icon: '💬', label: t('helpdesk.chip5') },
          ].map((f, i) => (
            <Chip key={i} label={`${f.icon} ${f.label}`} size="small"
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', border: '1px solid rgba(255,255,255,0.3)' }} />
          ))}
        </Box>
      </Paper>

      {alert.text && (
        <Alert severity={alert.type} sx={{ mb: 3 }} onClose={() => setAlert({ type: '', text: '' })}>
          {alert.text}
        </Alert>
      )}

      {/* ── Stats tickets ── */}
      {tickets.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: t('helpdesk.statTotal'), value: stats.total, color: 'primary' },
            { label: t('helpdesk.statOpen'), value: stats.open, color: 'error' },
            { label: t('helpdesk.statPending'), value: stats.pending, color: 'warning' },
            { label: t('helpdesk.statClosed'), value: stats.closed, color: 'success' },
          ].map(s => (
            <Grid item xs={6} sm={3} key={s.label}>
              <StatCard {...s} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* ── Tabs principaux ── */}
      <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
            <Tab icon={<NewReleases />} iconPosition="start" label={t('helpdesk.tabNewFeatures')} />
            <Tab icon={<Send />} iconPosition="start" label={t('helpdesk.tabNewTicket')} />
            <Tab icon={<ConfirmationNumber />} iconPosition="start"
              label={`${t('helpdesk.tabMyTickets')}${tickets.length ? ` (${tickets.length})` : ''}`} />
            <Tab icon={<QuestionAnswer />} iconPosition="start" label={t('helpdesk.tabFAQ')} />
          </Tabs>
        </Box>

        {/* ════ Tab 0 — Nouveautés ════ */}
        {tab === 0 && (
          <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <AutoAwesome color="primary" />
              <Typography variant="h6" fontWeight="bold">{t('helpdesk.newFeaturesTitle')}</Typography>
              <Chip label={t('helpdesk.updatedChip')} size="small" color="primary" icon={<FiberNew />} />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('helpdesk.newFeaturesDesc')}
            </Typography>

            <Grid container spacing={2}>
              {NOUVEAUTES.map((n, i) => (
                <Grid item xs={12} md={6} key={i}>
                  <Paper variant="outlined" sx={{ p: 2.5, height: '100%', borderRadius: 2, borderColor: n.color + '44',
                    '&:hover': { borderColor: n.color, boxShadow: `0 4px 20px ${n.color}22` }, transition: 'all 0.2s' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
                      <Avatar sx={{ bgcolor: n.color + '22', color: n.color, width: 40, height: 40 }}>
                        {n.icon}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle2" fontWeight="bold">{n.title}</Typography>
                          <Chip label={n.badge} size="small"
                            sx={{ height: 18, fontSize: '0.6rem',
                              bgcolor: n.badge === t('helpdesk.badgeNew') ? '#e8f5e9' : n.badge === t('helpdesk.badgeImproved') ? '#e3f2fd' : '#f3e5f5',
                              color: n.badge === t('helpdesk.badgeNew') ? '#2e7d32' : n.badge === t('helpdesk.badgeImproved') ? '#1565c0' : '#6a1b9a'
                            }} />
                        </Box>
                      </Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
                      {n.description}
                    </Typography>
                    <Box sx={{ bgcolor: '#f8f9ff', borderRadius: 1.5, p: 1.5 }}>
                      <Typography variant="caption" fontWeight="bold" color="primary" sx={{ display: 'block', mb: 0.5 }}>
                        <PlayCircle sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.5 }} />
                        {t('helpdesk.usageExamples')}
                      </Typography>
                      {n.exemples.map((ex, j) => (
                        <Typography key={j} variant="caption" color="text.secondary"
                          sx={{ display: 'flex', gap: 0.5, mt: 0.4, fontStyle: ex.startsWith('"') ? 'italic' : 'normal' }}>
                          <span style={{ color: n.color, flexShrink: 0 }}>›</span> {ex}
                        </Typography>
                      ))}
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            <Box sx={{ mt: 3, p: 2.5, bgcolor: '#fff8e1', borderRadius: 2, border: '1px solid #ffecb3' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Bolt sx={{ color: '#f57f17', fontSize: 20 }} />
                <Typography variant="subtitle2" fontWeight="bold" color="warning.dark">
                  {t('helpdesk.assistantTipTitle')}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {t('helpdesk.assistantTipDesc')}
              </Typography>
            </Box>
          </Box>
        )}

        {/* ════ Tab 1 — Nouveau ticket ════ */}
        {tab === 1 && (
          <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">{t('helpdesk.submitTitle')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('helpdesk.submitDesc')}
            </Typography>

            <form onSubmit={handleSubmit}>
              <Grid container spacing={2.5}>
                <Grid item xs={12}>
                  <TextField fullWidth required label={t('common.subject')}
                    placeholder={t('helpdesk.subjectPlaceholder')}
                    value={form.subject}
                    onChange={e => setForm(p => ({ ...p, subject: e.target.value }))}
                    inputProps={{ maxLength: 255 }}
                    helperText={`${form.subject.length}/255 ${t('helpdesk.characters')}`} />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>{t('helpdesk.department')}</InputLabel>
                    <Select value={form.department} label={t('helpdesk.department')}
                      onChange={e => setForm(p => ({ ...p, department: e.target.value }))}>
                      <MenuItem value="technical">🔧 {t('helpdesk.deptTechnical')} — {t('helpdesk.deptTechnicalDesc')}</MenuItem>
                      <MenuItem value="billing">💳 {t('helpdesk.deptBilling')} — {t('helpdesk.deptBillingDesc')}</MenuItem>
                      <MenuItem value="sales">📈 {t('helpdesk.deptSales')} — {t('helpdesk.deptSalesDesc')}</MenuItem>
                      <MenuItem value="general">💬 {t('helpdesk.deptGeneral')} — {t('helpdesk.deptGeneralDesc')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>{t('tasks.priority')}</InputLabel>
                    <Select value={form.priority} label={t('tasks.priority')}
                      onChange={e => setForm(p => ({ ...p, priority: e.target.value }))}>
                      <MenuItem value="low">🟢 {t('helpdesk.priorityLow')} — {t('helpdesk.priorityLowDesc')}</MenuItem>
                      <MenuItem value="medium">🔵 {t('helpdesk.priorityMedium')} — {t('helpdesk.priorityMediumDesc')}</MenuItem>
                      <MenuItem value="high">🟠 {t('helpdesk.priorityHigh')} — {t('helpdesk.priorityHighDesc')}</MenuItem>
                      <MenuItem value="urgent">🔴 {t('helpdesk.priorityUrgent')} — {t('helpdesk.priorityUrgentDesc')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField fullWidth required multiline rows={6} label={t('helpdesk.detailedDesc')}
                    placeholder={t('helpdesk.messagePlaceholder')}
                    value={form.message}
                    onChange={e => setForm(p => ({ ...p, message: e.target.value }))} />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Button type="submit" variant="contained" size="large"
                      startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <Send />}
                      disabled={loading}>
                      {loading ? t('helpdesk.sending') : t('helpdesk.sendTicket')}
                    </Button>
                    <Button variant="outlined" onClick={() => setForm(EMPTY_FORM)} disabled={loading}>
                      {t('common.clear')}
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                      {t('helpdesk.responseTime')}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </Box>
        )}

        {/* ════ Tab 2 — Mes tickets ════ */}
        {tab === 2 && (
          <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight="bold">{t('helpdesk.myTicketsTitle')}</Typography>
              <Button size="small" variant="outlined" onClick={loadTickets} disabled={ticketsLoading}>
                {ticketsLoading ? <CircularProgress size={16} /> : t('common.refresh')}
              </Button>
            </Box>

            {ticketsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                <CircularProgress />
              </Box>
            ) : tickets.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <ConfirmationNumber sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>{t('helpdesk.noTickets')}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {t('helpdesk.noTicketsDesc')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                  <Button variant="contained" onClick={() => setTab(1)}>{t('helpdesk.submitTicket')}</Button>
                  <Button variant="outlined" onClick={() => setTab(3)}>{t('helpdesk.seeFAQ')}</Button>
                </Box>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>{t('common.subject')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>{t('helpdesk.department')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>{t('tasks.priority')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>{t('common.status')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>{t('helpdesk.colDate')}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>{t('helpdesk.colDetail')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tickets.map(tick => (
                      <TableRow key={tick.id} hover sx={{ cursor: 'pointer' }}
                        onClick={() => ticketService.getById(tick.id).then(r => setViewTicket(r.data.ticket)).catch(() => setViewTicket(tick))}>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary" fontWeight="bold">#{tick.id}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {tick.subject}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {DEPT_LABELS[tick.department] || tick.department}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={PRIORITY_LABELS[tick.priority] || tick.priority}
                            size="small" color={PRIORITY_COLORS[tick.priority]} />
                        </TableCell>
                        <TableCell>
                          <Chip icon={STATUS_ICONS[tick.status]}
                            label={STATUS_LABELS[tick.status]}
                            size="small" color={STATUS_COLORS[tick.status]}
                            variant={tick.status === 'closed' ? 'outlined' : 'filled'} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(tick.created_at)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Button size="small" variant="outlined">{t('helpdesk.view')}</Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {/* ════ Tab 3 — FAQ ════ */}
        {tab === 3 && (
          <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <QuestionAnswer color="primary" />
              <Typography variant="h6" fontWeight="bold">{t('helpdesk.faqTitle')}</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('helpdesk.faqDesc')}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
              {FAQ_CATEGORIES.map((cat, i) => (
                <Chip key={i}
                  icon={React.cloneElement(cat.icon, { sx: { fontSize: '16px !important' } })}
                  label={cat.label}
                  onClick={() => setFaqCategory(i)}
                  variant={faqCategory === i ? 'filled' : 'outlined'}
                  sx={{
                    cursor: 'pointer',
                    bgcolor: faqCategory === i ? cat.color : 'transparent',
                    color: faqCategory === i ? 'white' : cat.color,
                    borderColor: cat.color,
                    '&:hover': { bgcolor: cat.color + '22' }
                  }} />
              ))}
            </Box>

            <Box>
              {FAQ_CATEGORIES[faqCategory].items.map((faq, i) => (
                <Accordion key={i} elevation={0} disableGutters
                  sx={{ border: '1px solid #e0e0e0', mb: 1, borderRadius: '8px !important',
                    '&:before': { display: 'none' }, overflow: 'hidden' }}>
                  <AccordionSummary expandIcon={<ExpandMore />}
                    sx={{ '&:hover': { bgcolor: FAQ_CATEGORIES[faqCategory].color + '08' } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%',
                        bgcolor: FAQ_CATEGORIES[faqCategory].color, flexShrink: 0 }} />
                      <Typography variant="body2" fontWeight="medium">{faq.q}</Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ bgcolor: '#fafafa', borderTop: '1px solid #e0e0e0' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                      {faq.a}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>

            <Divider sx={{ my: 3 }} />

            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, bgcolor: '#f8f9ff', borderColor: '#c5cae9' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" fontWeight="bold">{t('helpdesk.notFound')}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('helpdesk.notFoundDesc')}
                  </Typography>
                </Box>
                <Button variant="contained" startIcon={<Send />} onClick={() => setTab(1)}>
                  {t('helpdesk.contactSupport')}
                </Button>
              </Box>
            </Paper>
          </Box>
        )}
      </Paper>

      {/* ── Dialog détail ticket ── */}
      <Dialog open={!!viewTicket} onClose={() => setViewTicket(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ConfirmationNumber color="primary" />
            <Typography fontWeight="bold">{t('helpdesk.ticketNumber', { id: viewTicket?.id })}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="h6" gutterBottom>{viewTicket?.subject}</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <Chip label={PRIORITY_LABELS[viewTicket?.priority] || viewTicket?.priority}
              size="small" color={PRIORITY_COLORS[viewTicket?.priority]} />
            <Chip icon={STATUS_ICONS[viewTicket?.status]}
              label={STATUS_LABELS[viewTicket?.status]}
              size="small" color={STATUS_COLORS[viewTicket?.status]} />
            <Chip label={DEPT_LABELS[viewTicket?.department] || viewTicket?.department}
              size="small" variant="outlined" />
          </Box>
          <Typography variant="caption" color="text.secondary">
            {t('helpdesk.submittedOn')} {viewTicket?.created_at ? formatDate(viewTicket.created_at) : ''}
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fafafa', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
              {viewTicket?.message}
            </Typography>
          </Paper>
          {viewTicket?.status === 'closed' && (
            <Alert severity="success" icon={<CheckCircle />} sx={{ mt: 2 }}>
              {t('helpdesk.ticketClosed')}
            </Alert>
          )}
          {viewTicket?.status === 'pending' && (
            <Alert severity="info" icon={<HourglassEmpty />} sx={{ mt: 2 }}>
              {t('helpdesk.ticketPending')}
            </Alert>
          )}
          {viewTicket?.status === 'new' && (
            <Alert severity="warning" icon={<Schedule />} sx={{ mt: 2 }}>
              {t('helpdesk.ticketNew')}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewTicket(null)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
